import React, { useState, useEffect, useCallback, useRef } from 'react';
import { httpSymfony } from 'Utils/http';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import {
    Box,
    FormHelperText,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Checkbox,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
    expandTabs,
    distributeDataToTab,
    collectDataFromTabs,
    cleanFieldName,
    getTabIndex,
    getOriginalTabName,
    hasAnyArrayMarkers,
    extractHintText
} from 'Utils/tabArrayUtils';

const EXCLUDE_MARKER = '_EXCLUDED_';
const buildExcludeKey = (tabName, cleanName) => `${tabName}::${cleanName}`;

const editorStyles = {
    marginBottom: '16px',
    borderRadius: '4px',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderWidth: '1px',
    borderStyle: 'solid',
    backgroundColor: '#F2F5F8',
    minHeight: '100px',
    fontFamily: '"Fira code", "Fira Mono", monospace',
    fontSize: 14,
};

const labelStyles = {
    position: 'absolute',
    top: '-8px',
    left: '8px',
    padding: '0 4px',
    borderRadius: '2px',
    fontSize: '0.75rem',
    color: 'rgba(0, 0, 0, 0.6)',
    backgroundColor: 'white',
    zIndex: '2',
};

const tabStyles = {
    minWidth: '200px',
    '& .MuiButtonBase-root': {
        alignItems: 'baseline',
    },
};

const buttonStyles = {
    padding: '16px 26px',
    fontSize: '16px',
    borderRadius: '4px',
};

const a11yProps = (index) => ({
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
});

const TabPanel = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
        style={{ width: '100%' }}
    >
        <Box sx={{ padding: '0px 24px 24px 24px' }}>
            {children}
        </Box>
    </div>
);

const UploadLoanReworkedWithTabs = ({ responseLoan, flowsList, setResponseJson, tabsConfig, fieldsConfig }) => {

    const [tabs, setTabs] = useState(null);
    const [fields, setFields] = useState(null);
    const [originalTabsConfig, setOriginalTabsConfig] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [tabDataMap, setTabDataMap] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [fieldTypes, setFieldTypes] = useState({});
    const [formattedValues, setFormattedValues] = useState({});
    const [excludedFields, setExcludedFields] = useState({});
    const focusedFieldsRef = useRef({});
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const {
        control,
        watch,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const handleTabChange = (event, newValue) => {
        // Check if we have any expanded tabs (tabs with array markers)
        const hasExpandedTabs = originalTabsConfig && hasAnyArrayMarkers(originalTabsConfig);

        if (hasExpandedTabs) {
            // Get current form values
            const currentValues = watch();

            // Save flow_id before any operations
            const currentFlowId = currentValues.flow_id;

            // Determine which tab index we're on
            const currentTabIndex = getTabIndex(Object.keys(tabs || {})[activeTab] || '');
            const newTabIndex = getTabIndex(Object.keys(tabs || {})[newValue] || '');

            // Update tabDataMap with current values and load new tab data
            // Using functional update to avoid stale closure
            setTabDataMap(prev => {
                const updatedMap = {
                    ...prev,
                    [currentTabIndex]: currentValues
                };

                // Load data for new tab from updated map
                const newTabData = updatedMap[newTabIndex] || {};

                // Reset form with new tab data
                reset({ ...newTabData, flow_id: currentFlowId });

                return updatedMap;
            });

            // Switch to new tab
            setActiveTab(newValue);
        } else {
            // Switch to new tab
            setActiveTab(newValue);
        }
    };

    const handleExcludeFieldChange = (fieldName, isExcluded) => {
        setExcludedFields(prev => ({
            ...prev,
            [fieldName]: isExcluded
        }));
    };

    const determineFieldType = (fieldName, value) => {
        let currentFieldConfig = null;
        for (const sectionKey in fieldsConfig) {
            if (fieldsConfig[sectionKey]?.[fieldName]?.type === 'select') {
                currentFieldConfig = fieldsConfig[sectionKey][fieldName];
                break;
            }
        }

        if (!currentFieldConfig && fieldName.includes('.')) {
            const [section, field] = fieldName.split('.');
            currentFieldConfig = fieldsConfig?.[section]?.[field];
        }

        if (currentFieldConfig?.type === 'select') {
            return 'select';
        }

        const trimmed = typeof value === 'string' ? value.trim() : '';

        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                const parsed = JSON.parse(trimmed);
                const isObjectLike = typeof parsed === 'object' && parsed !== null;
                if (isObjectLike) {
                    return 'object';
                }
            } catch (e) {}
        }

        if (trimmed === '') {
            return fieldTypes[fieldName] || 'string';
        }

        return 'string';
    };

    const deepParseJSON = useCallback((obj) => {
        const result = { ...obj };
        for (const key in result) {
            if (typeof result[key] === 'string') {
                try {
                    result[key] = JSON.parse(result[key]);
                    if (typeof result[key] === 'object' && result[key] !== null) {
                        result[key] = deepParseJSON(result[key]);
                    }
                } catch (e) {}
            } else if (typeof result[key] === 'object' && result[key] !== null) {
                result[key] = deepParseJSON(result[key]);
            }
        }
        return result;
    }, []);

    const onSubmit = async (data) => {
        dispatch(setLoading(true));
        try {
            const { flow_id, ...clonedObject } = data;

            let finalObject = clonedObject;

            // If we have tabs with array markers (expanded tabs), collect data back
            const needsCollection = originalTabsConfig && tabs && hasAnyArrayMarkers(originalTabsConfig);

            if (needsCollection) {
                // IMPORTANT: Save current active tab data before collecting
                // (user might have edited current tab without switching to another)
                const currentTabIndex = getTabIndex(Object.keys(tabs || {})[activeTab] || '');
                const currentValues = watch();
                const updatedTabDataMap = {
                    ...tabDataMap,
                    [currentTabIndex]: currentValues
                };

                // Collect data from all tabs back into arrays
                const getTabData = (tabIndex) => updatedTabDataMap[tabIndex] || {};

                finalObject = collectDataFromTabs(tabs, originalTabsConfig, getTabData);
            }

            // Apply per-tab exclusion: substitute _EXCLUDED_ for excluded array elements,
            // drop scalars; later drop fields whose array is fully _EXCLUDED_.
            const processedObject = {};

            if (needsCollection) {
                const expandedTabsConfig = expandTabs(originalTabsConfig, responseLoan);
                Object.entries(expandedTabsConfig).forEach(([expandedTabName, fieldNames]) => {
                    if (!Array.isArray(fieldNames)) return;
                    const tabIndex = getTabIndex(expandedTabName);
                    fieldNames.forEach(fieldName => {
                        const cleanName = cleanFieldName(fieldName);
                        if (!(cleanName in finalObject)) return;
                        const isExcluded = !!excludedFields[buildExcludeKey(expandedTabName, cleanName)];
                        const value = finalObject[cleanName];
                        if (Array.isArray(value)) {
                            if (processedObject[cleanName] === undefined) {
                                processedObject[cleanName] = [...value];
                            }
                            if (isExcluded) {
                                processedObject[cleanName][tabIndex] = EXCLUDE_MARKER;
                            }
                        } else if (processedObject[cleanName] === undefined && !isExcluded) {
                            processedObject[cleanName] = value;
                        }
                    });
                });
            } else if (originalTabsConfig) {
                Object.entries(originalTabsConfig).forEach(([tabName, fieldNames]) => {
                    if (!Array.isArray(fieldNames)) return;
                    fieldNames.forEach(fieldName => {
                        const cleanName = cleanFieldName(fieldName);
                        if (!(cleanName in finalObject)) return;
                        if (!excludedFields[buildExcludeKey(tabName, cleanName)]) {
                            processedObject[cleanName] = finalObject[cleanName];
                        }
                    });
                });
            } else {
                Object.keys(finalObject).forEach(cleanName => {
                    if (!excludedFields[buildExcludeKey('', cleanName)]) {
                        processedObject[cleanName] = finalObject[cleanName];
                    }
                });
            }

            const filteredObject = Object.keys(processedObject).reduce((acc, key) => {
                const value = processedObject[key];
                if (Array.isArray(value) && value.every(v => v === EXCLUDE_MARKER)) {
                    return acc;
                }
                acc[key] = value;
                return acc;
            }, {});

            const transformData = {
                jsonContent: JSON.stringify(deepParseJSON(filteredObject)),
                flow_id: flow_id,
            };

            const response = await httpSymfony.post(`/call/flow/${data.flow_id}`, transformData);

            enqueueSnackbar('Data submitted', {
                action: CustomSnackbar,
                variant: 'success',
            });

            setResponseJson(JSON.stringify(response.data));
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to import loan';
            enqueueSnackbar(errorMessage, {
                action: CustomSnackbar,
                variant: 'error'
            });
        } finally {
            dispatch(setLoading(false));
        }
    };

    useEffect(() => {
        if (!tabsConfig || !responseLoan) {
            setTabs(tabsConfig || null);
            setOriginalTabsConfig(tabsConfig || null);
            setFields(fieldsConfig || null);
            // Clear cache when form is cleared
            setActiveTab(0);
            setTabDataMap({});
            return;
        }

        // Store original configuration
        setOriginalTabsConfig(tabsConfig);
        setFields(fieldsConfig || null);

        // Expand tabs if array markers exist
        const expandedTabsConfig = expandTabs(tabsConfig, responseLoan);
        setTabs(expandedTabsConfig);

        // Reset to first tab when new form is loaded
        setActiveTab(0);
        // Clear old tab data cache to prevent stale data from previous form
        setTabDataMap({});
    }, [tabsConfig, fieldsConfig, responseLoan]);

    useEffect(() => {
        // Save current flow_id before any reset operations
        const currentFlowId = watch('flow_id');

        if (!responseLoan) {
            reset({ flow_id: currentFlowId });
            setFormattedValues({});
            setFieldTypes({});
            setExcludedFields({});
            setOriginalData(null);
            setTabDataMap({});
            return;
        }

        // Store original data
        setOriginalData(responseLoan);

        // Check if we have any tabs with array markers
        const needsDistribution = originalTabsConfig && hasAnyArrayMarkers(originalTabsConfig);

        if (needsDistribution && originalTabsConfig) {
            // Get expanded tabs to know how many tab instances we need to create
            const expandedTabsConfig = expandTabs(originalTabsConfig, responseLoan);

            // Find the maximum number of tab instances (e.g., "Tab 1", "Tab 2", "Tab 3")
            let maxTabIndex = 0;
            Object.keys(expandedTabsConfig).forEach(tabName => {
                const match = tabName.match(/^(.+)\s+(\d+)$/);
                if (match) {
                    const index = parseInt(match[2], 10) - 1;
                    maxTabIndex = Math.max(maxTabIndex, index);
                }
            });
            const maxTabs = maxTabIndex + 1;

            // Distribute data across multiple tabs
            const newTabDataMap = {};
            const allFormattedValues = {};
            const allFieldTypes = {};
            const initialExcludedFields = {};

            for (let tabIndex = 0; tabIndex < maxTabs; tabIndex++) {
                const tabData = distributeDataToTab(responseLoan, originalTabsConfig, tabIndex);
                newTabDataMap[tabIndex] = tabData;

                // Process each field in this tab
                Object.keys(tabData).forEach(key => {
                    const value = tabData[key];
                    const isObject = value !== null && typeof value === 'object';

                    const isSelect = Object.values(fields || {}).some(group =>
                        group[key]?.type === 'select'
                    );

                    let formattedValue;
                    if (isObject) {
                        formattedValue = JSON.stringify(value, null, 4);
                    } else if (value === null) {
                        formattedValue = 'data not found';
                    } else {
                        formattedValue = value;
                    }

                    let fieldType;
                    if (isSelect) {
                        fieldType = 'select';
                    } else if (isObject) {
                        fieldType = 'object';
                    } else if (value === null) {
                        fieldType = 'string';
                    } else {
                        fieldType = typeof value;
                    }

                    allFieldTypes[key] = fieldType;
                    allFormattedValues[key] = formattedValue;
                });
            }

            Object.entries(expandedTabsConfig).forEach(([expandedTabName, fieldNames]) => {
                if (!Array.isArray(fieldNames)) return;
                const tabIndex = getTabIndex(expandedTabName);
                const tabData = newTabDataMap[tabIndex] || {};
                fieldNames.forEach(fieldName => {
                    const cleanName = cleanFieldName(fieldName);
                    const value = tabData[cleanName];
                    const normalizedValue = typeof value === 'string' ? value.trim() : '';
                    const isExcludedByDefault = normalizedValue.toLowerCase() === 'excluded by default';
                    const isMarker = value === EXCLUDE_MARKER;
                    if (value === null || isExcludedByDefault || isMarker) {
                        initialExcludedFields[buildExcludeKey(expandedTabName, cleanName)] = true;
                    }
                });
            });

            setTabDataMap(newTabDataMap);
            setFormattedValues(allFormattedValues);
            setFieldTypes(allFieldTypes);
            setExcludedFields(initialExcludedFields);

            // Reset form with data from first tab + preserve flow_id
            reset({ ...newTabDataMap[0], flow_id: currentFlowId });
        } else {
            // No distribution needed - use original logic
            const formatted = {};
            const types = {};
            const initialValues = {};
            const initialExcludedFields = {};

            Object.keys(responseLoan).forEach(key => {
                const value = responseLoan[key];
                const isObject = value !== null && typeof value === 'object';

                const isSelect = Object.values(fields || {}).some(group =>
                    group[key]?.type === 'select'
                );

                let formattedValue;
                if (isObject) {
                    formattedValue = JSON.stringify(value, null, 4);
                } else if (value === null) {
                    formattedValue = 'data not found';
                } else {
                    formattedValue = value;
                }

                let fieldType;
                if (isSelect) {
                    fieldType = 'select';
                } else if (isObject) {
                    fieldType = 'object';
                } else if (value === null) {
                    fieldType = 'string';
                } else {
                    fieldType = typeof value;
                }

                types[key] = fieldType;
                formatted[key] = formattedValue;
                initialValues[key] = formatted[key];
            });

            const markExcluded = (tabName, cleanName) => {
                const value = responseLoan[cleanName];
                const normalizedValue = typeof value === 'string' ? value.trim() : '';
                const isExcludedByDefault = normalizedValue.toLowerCase() === 'excluded by default';
                const isMarker = value === EXCLUDE_MARKER;
                if (value === null || isExcludedByDefault || isMarker) {
                    initialExcludedFields[buildExcludeKey(tabName, cleanName)] = true;
                }
            };

            if (originalTabsConfig) {
                Object.entries(originalTabsConfig).forEach(([tabName, fieldNames]) => {
                    if (!Array.isArray(fieldNames)) return;
                    fieldNames.forEach(fieldName => {
                        markExcluded(tabName, cleanFieldName(fieldName));
                    });
                });
            } else {
                Object.keys(responseLoan).forEach(key => markExcluded('', key));
            }

            setFormattedValues(formatted);
            setFieldTypes(types);
            setExcludedFields(initialExcludedFields);
            // Preserve flow_id when resetting
            reset({ ...initialValues, flow_id: currentFlowId });
        }
    }, [responseLoan, reset, fields, originalTabsConfig, watch]);


        useEffect(() => {
            const subscription = watch((values, { name }) => {
                const value = values[name];

            setFormattedValues(prev => ({
                ...prev,
                [name]: value
            }));
            if (name) {
                let currentFieldConfig = null;

                for (const sectionKey in fieldsConfig) {
                    if (fieldsConfig[sectionKey]?.[name]?.type === 'select') {
                        currentFieldConfig = fieldsConfig[sectionKey][name];
                        break;
                    }
                }

                if (!currentFieldConfig && name.includes('.')) {
                    const [section, field] = name.split('.');
                    currentFieldConfig = fieldsConfig?.[section]?.[field];
                }

                if (currentFieldConfig?.type === 'select') {
                    return;
                }
            }
            const trimmed = typeof value === 'string' ? value.trim() : '';

            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
                (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                try {
                    const parsed = JSON.parse(trimmed);
                    const isObjectLike = typeof parsed === 'object' && parsed !== null;
                    if (isObjectLike && !focusedFieldsRef.current[name]) {
                        setFieldTypes(prev => ({
                            ...prev,
                            [name]: 'object'
                        }));
                    }
                } catch (e) {}
            } else if (trimmed === '') {
            } else {
                if (!focusedFieldsRef.current[name]) {
                    setFieldTypes(prev => ({
                        ...prev,
                        [name]: 'string'
                    }));
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, fieldsConfig]);

    useEffect(() => {
        if (!responseLoan) {
            reset();
        }
    }, [reset, responseLoan]);

    const renderSelectField = (fieldName, fieldProps, hintText = null, excludeKey = '') => {
        const selectOptions = Object.values(fields || {}).flatMap(group =>
            group[fieldName]?.value || []
        );
        const isExcluded = !!excludedFields[excludeKey];

        return (
            <TextField
                {...fieldProps}
                select
                label={cleanFieldName(fieldName)}
                fullWidth
                disabled={isExcluded}
                InputLabelProps={{
                    shrink: true
                }}
                sx={{
                    mb: 2,
                    ...(isExcluded && {
                        backgroundColor: '#f5f5f5',
                        '& .MuiInputBase-input': {
                            color: '#999'
                        }
                    })
                }}
                error={!!errors[fieldName]}
                helperText={hintText || (errors[fieldName] ? 'This is a required field' : '')}
            >
                {selectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderObjectField = (fieldName, fieldProps, hintText = null, excludeKey = '') => {
        const isExcluded = !!excludedFields[excludeKey];
        const { onChange } = fieldProps;
        const handleEditorChange = (code) => {
            try {
                if (code.trim() === '') {
                    onChange('');
                    return;
                }
                const parsedValue = JSON.parse(code);
                if (Array.isArray(parsedValue)) {
                    onChange(JSON.stringify(parsedValue, null, 4));
                    return;
                }
                if (typeof parsedValue === 'string') {
                    onChange(parsedValue);
                    return;
                }
                onChange(JSON.stringify(parsedValue, null, 4));
            } catch (error) {
                onChange(code);
            }
        };

        return (
            <Box position="relative">
                <Typography component="span" sx={labelStyles}>
                    {cleanFieldName(fieldName)}
                </Typography>
                <div
                    onFocus={() => {
                        focusedFieldsRef.current[fieldName] = true;
                    }}
                    onBlur={() => {
                        focusedFieldsRef.current[fieldName] = false;
                        const currentValue = formattedValues[fieldName];
                        const newType = determineFieldType(fieldName, currentValue);
                        setFieldTypes(prev => ({
                            ...prev,
                            [fieldName]: newType
                        }));
                    }}
                >
                    <Editor
                        {...fieldProps}
                        tabSize={4}
                        padding={10}
                        value={formattedValues[fieldName] || ''}
                        label={fieldName}
                        insertSpaces={true}
                        onValueChange={handleEditorChange}
                        highlight={(code) => highlight(code, languages.js)}
                        style={{
                            ...editorStyles,
                            ...(isExcluded && {
                                backgroundColor: '#f5f5f5',
                                color: '#999',
                                cursor: 'not-allowed'
                            })
                        }}
                        readOnly={isExcluded}
                    />
                </div>
                {hintText && (
                    <FormHelperText sx={{ mt: 0.5 }}>{hintText}</FormHelperText>
                )}
            </Box>
        )
    };

    const renderTextField = (fieldName, fieldProps, hintText = null, excludeKey = '') => {
        const isExcluded = !!excludedFields[excludeKey];
        return (
        <TextField
                {...fieldProps}
                type="text"
                label={cleanFieldName(fieldName)}
                disabled={isExcluded}
                InputLabelProps={{
                    shrink: true
                }}
                onFocus={() => {
                    focusedFieldsRef.current[fieldName] = true;
                }}
                onBlur={() => {
                    focusedFieldsRef.current[fieldName] = false;
                    const currentValue = formattedValues[fieldName];
                    const newType = determineFieldType(fieldName, currentValue);
                    setFieldTypes(prev => ({
                        ...prev,
                        [fieldName]: newType
                    }));
                }}
                sx={{
                    marginBottom: '16px',
                    ...(isExcluded && {
                        backgroundColor: '#f5f5f5',
                        '& .MuiInputBase-input': {
                            color: '#999'
                        }
                    })
                }}
                error={!!errors[fieldName]}
                helperText={hintText || (errors[fieldName] ? 'This is a required field' : '')}
                fullWidth
        />
        );
    };

    const renderFormField = (fieldName, tabName) => {
        // Clean field name to remove [] marker and extract hint
        const cleanName = cleanFieldName(fieldName);
        const type = fieldTypes[cleanName];
        const hintText = extractHintText(fieldName);
        const excludeKey = buildExcludeKey(tabName, cleanName);

        return (
            <Box key={fieldName} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Controller
                        name={cleanName}
                        control={control}
                        defaultValue={formattedValues[cleanName] || ''}
                        render={({ field }) => {
                            if (type === 'object') return renderObjectField(cleanName, field, hintText, excludeKey);
                            if (type === 'select') return renderSelectField(cleanName, field, hintText, excludeKey);
                            return renderTextField(cleanName, field, hintText, excludeKey);
                        }}
                    />

                {errors[cleanName] && (
                    <FormHelperText error>{errors[cleanName].message}</FormHelperText>
                )}
            </Box>
            {responseLoan && (
                <Box sx={{ minWidth: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Checkbox
                        checked={excludedFields[excludeKey] || false}
                        onChange={(e) => handleExcludeFieldChange(excludeKey, e.target.checked)}
                    />
                </Box>
            )}
        </Box>
        );
    };

    const renderTabContent = (fields, tabName) => (
        <Box>
            {fields.map(fieldName => renderFormField(fieldName, tabName))}
        </Box>
    );

    const renderFlowSelector = () => (
        <Box display="flex" mt={2}>
            <FormControl sx={{ minWidth: 220, mr: 2 }} error={!!errors.flow_id}>
                <InputLabel>Select Flow to process file</InputLabel>
                <Controller
                    name="flow_id"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <Select {...field} label="Select Flow to process file">
                            {flowsList?.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
                {errors.flow_id && (
                    <FormHelperText sx={{ position: 'absolute', bottom: '-20px' }}>
                        This is a required field
                    </FormHelperText>
                )}
            </FormControl>
            <Button
                sx={buttonStyles}
                color="primary"
                variant="contained"
                type="submit"
            >
                Submit Data
            </Button>
        </Box>
    );

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
            }
        }}>
            {tabs ? (
                <Box sx={{ flexGrow: 1, display: 'flex', mb: 3 }}>
                    <Tabs
                        orientation="vertical"
                        variant="scrollable"
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={tabStyles}
                    >
                        {Object.keys(tabs).map((tabName, index) => (
                            <Tab label={tabName} {...a11yProps(index)} key={tabName} />
                        ))}
                    </Tabs>

                    {Object.keys(tabs).map((tabName, index) => (
                        <TabPanel value={activeTab} index={index} key={tabName}>
                            {renderTabContent(tabs[tabName], tabName)}
                        </TabPanel>
                    ))}
                </Box>
            ) : (
                responseLoan && renderTabContent(Object.keys(responseLoan), '')
            )}

            {responseLoan && renderFlowSelector()}
        </Box>
    );
};

export default UploadLoanReworkedWithTabs;
